<script setup lang="ts">
import type { CmsElementForm } from "@shopware/composables"
import { ApiClientError } from "@shopware/api-client"

const props = defineProps<{
    content?: CmsElementForm
}>()

// Composables
const { t } = useI18n()
const { getSalutations } = useSalutations()
const { apiClient } = useShopwareContext()
const { foreignKey } = useNavigationContext()
const { resolveApiErrors } = useApiErrorsResolver()
const { recaptcha } = useRecaptchaV3()

// State
const isLoading = ref(false)
const formSent = ref(false)
const formError = ref<string | null>(null)
const formData = reactive({
    salutationId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    comment: '',
    checkbox: false
})

// Computed
const title = computed(() => {
    return props.content?.config?.title?.value ?? t('contact.headline')
})

const confirmationText = computed(() => {
    return props.content?.config?.confirmationText?.value ?? t('contact.successMessage')
})

const salutationOptions = computed(() =>
    getSalutations.value.map(salutation => ({
        value: salutation.id,
        label: salutation.displayName
    }))
)

const isFormValid = computed(() => {
    const requiredFields = [
        formData.salutationId,
        formData.firstName,
        formData.lastName,
        formData.email,
        formData.phone,
        formData.subject,
        formData.comment
    ]

    const allFieldsFilled = requiredFields.every(field => field.trim().length > 0)
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    const isFirstNameValid = formData.firstName.trim().length >= 3
    const isLastNameValid = formData.lastName.trim().length >= 3
    const isSubjectValid = formData.subject.trim().length >= 3
    const isCommentValid = formData.comment.trim().length >= 10
    const isPrivacyAccepted = formData.checkbox

    return allFieldsFilled && 
           isEmailValid && 
           isFirstNameValid && 
           isLastNameValid &&
           isSubjectValid && 
           isCommentValid && 
           isPrivacyAccepted && 
           !isLoading.value
})

const handleSubmit = async () => {
    if (!isFormValid.value) return
    isLoading.value = true
    formError.value = null

    try {
        const isNotABot = await recaptcha()

        if (!isNotABot) {
            formError.value = t('contact.errorMessage')
            return
        }

        await apiClient.invoke("sendContactMail post /contact-form", {
            body: {
                ...formData,
                navigationId: foreignKey.value,
            },
        })
        formSent.value = true
    } catch (apiError) {
        if (apiError instanceof ApiClientError) {
            const errorMessages = resolveApiErrors(apiError.details.errors)
            formError.value = errorMessages.join(', ') || t('contact.errorMessage')
        } else {
            formError.value = apiError instanceof Error ? apiError.message : t('contact.errorMessage')
        }
    } finally {
        isLoading.value = false
    }
}
</script>

<template>
    <div>
        <FoundationHeadline tag="h3" class="px-4 -ml-4 text-lg font-medium mb-2">
            {{ title }}
        </FoundationHeadline>

        <div v-if="formSent" class="flex items-start gap-3 rounded-lg bg-success/10 border border-success p-4 text-success" role="alert">
            <FoundationIcon name="circle-check" class="mt-0.5 shrink-0" />
            <p>{{ confirmationText }}</p>
        </div>

        <form v-else @submit.prevent="handleSubmit">
            <div class="space-y-6">
                <div class="flex flex-col gap-4">
                    <!-- Salutation, First Name, Last Name Row -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Salutation -->
                        <div class="w-full">
                            <FoundationLabel for="contact-salutation" class="block sr-only" required>
                                {{ $t('form.salutation') }}
                                <span aria-hidden="true">{{ $t('form.required') }}</span>
                            </FoundationLabel>
                            <FoundationSelect
                                id="contact-salutation"
                                v-model="formData.salutationId"
                                :placeholder="$t('form.salutation')+$t('form.required')"
                                :options="salutationOptions"
                                required
                                :disabled="isLoading"
                                name="salutation"
                                class="w-full"
                            />
                        </div>

                        <!-- First Name -->
                        <ComponentTextInput
                            id="contact-first-name"
                            v-model="formData.firstName"
                            :label="$t('form.firstName')+$t('form.required')"
                            type="text"
                            :placeholder="$t('form.firstName')+$t('form.required')"
                            required
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />

                        <!-- Last Name -->
                        <ComponentTextInput
                            id="contact-last-name"
                            v-model="formData.lastName"
                            :label="$t('form.lastName')+$t('form.required')"
                            type="text"
                            :placeholder="$t('form.lastName')+$t('form.required')"
                            required
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />
                    </div>

                    <!-- Email and Phone Row -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <!-- Email -->
                        <ComponentTextInput
                            id="contact-email"
                            v-model="formData.email"
                            :label="$t('form.email')+$t('form.required')"
                            type="email"
                            :placeholder="$t('form.email')+$t('form.required')"
                            required
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />

                        <!-- Phone -->
                        <ComponentTextInput
                            id="contact-phone"
                            v-model="formData.phone"
                            :label="$t('contact.phone')"
                            type="tel"
                            :placeholder="$t('contact.phone')"
                            required
                            :disabled="isLoading"
                            size="md"
                            bordered
                        />
                    </div>

                    <!-- Subject -->
                    <ComponentTextInput
                        id="contact-subject"
                        v-model="formData.subject"
                        :label="$t('contact.subject')+$t('form.required')"
                        type="text"
                        :placeholder="$t('contact.subject')+$t('form.required')"
                        required
                        :disabled="isLoading"
                        size="md"
                        bordered
                    />

                    <!-- Message/Comment -->
                    <ComponentTextarea
                        id="contact-comment"
                        v-model="formData.comment"
                        :label="$t('contact.message')+$t('form.required')"
                        :placeholder="$t('contact.messagePlaceholder')"
                        :rows="5"
                        required
                        :disabled="isLoading"
                        bordered
                    />

                    <!-- Data Protection Checkbox -->
                    <div class="flex items-center">
                        <FoundationCheckbox
                            id="contact-data-protection"
                            v-model="formData.checkbox"
                            :disabled="isLoading"
                            required
                        />
                        <FoundationLabel for="contact-data-protection" class="ml-2 text-sm" required>
                            {{ $t('contact.dataProtection') }}
                            <span aria-hidden="true">{{ $t('form.required') }}</span>
                        </FoundationLabel>
                    </div>

                    <p>
                        {{ $t('form.recaptchaHint') }}
                        <FoundationLink is="external" to="https://policies.google.com/privacy" target="_blank">
                            {{ $t('form.recaptchaPrivatePolicy') }}
                        </FoundationLink>
                        {{ $t('form.recaptchaAnd') }}
                        <FoundationLink is="external" to="https://policies.google.com/terms" target="_blank">
                            {{ $t('form.recaptchaTac') }}
                        </FoundationLink>
                        {{ $t('form.recaptchaOf') }}
                    </p>

                    <!-- Error Message -->
                    <div v-if="formError" class="flex items-start gap-3 rounded-lg bg-error/10 border border-error p-4 text-error" role="alert">
                        <FoundationIcon name="circle-x" class="mt-0.5 shrink-0" />
                        <p>{{ formError }}</p>
                    </div>

                    <!-- Submit Button -->
                    <div class="flex flex-col sm:flex-row gap-3 pt-4">
                        <FoundationButton
                            type="submit"
                            color="secondary"
                            class="flex-1"
                            :loading="isLoading"
                            :disabled="!isFormValid"
                            @click="handleSubmit"
                        >
                            {{ $t('contact.submit') }}
                        </FoundationButton>
                    </div>

                    <div class="text-sm">{{ $t('form.requiredFieldsInfo') }}</div>
                </div>
            </div>
        </form>
    </div>
</template>
